import React, { useCallback, useMemo, useState } from "react";
import type {
  Client,
  Conversation as SdkConversation,
} from "@twilio/conversations";

import MessagesBox from "../../message/MessagesBox";
import type { ReduxParticipant } from "../../../store/reducers/participantsReducer";
import type { ReduxMessage } from "../../../store/reducers/messageListReducer";
import type { AddMessagesType } from "../../../types";

import { mapSdkConversationToRedux } from "./mapSdkConversation";
import NewMessageInputField from "./NewMessageInputField";

type Props = {
  sid: string;
  id: string;
  client: Client;
  templateOnly: boolean;
  inputDisabled: boolean;
  sdkConvo: SdkConversation;
  messages: ReduxMessage[];
  participants: ReduxParticipant[];
  typingData: any[];
  lastReadIndex: number;
  loadingStatus: boolean;
  use24hTimeFormat: boolean;
  droppedFiles: File[];
  onDroppedFilesChange: (files: File[]) => void;
  pushMessages: AddMessagesType;
};

const NewConversationContentInner: React.FC<Props> = (props) => {
  const selectedSid = props.sid;

  const [draftsBySid, setDraftsBySid] = useState<Record<string, string>>({});

  const draftValue = draftsBySid[selectedSid] ?? "";

  const setDraftValue = useCallback(
    (value: string) => {
      setDraftsBySid((prev) => ({
        ...prev,
        [selectedSid]: value,
      }));
    },
    [selectedSid]
  );

  const reduxConvo = useMemo(
    () => mapSdkConversationToRedux(props.sdkConvo),
    [props.sdkConvo.sid]
  );

  return (
    <>
      <MessagesBox
        key={selectedSid}
        convoSid={selectedSid}
        convo={reduxConvo}
        upsertMessage={props.pushMessages}
        client={props.client}
        messages={props.messages}
        loadingState={props.loadingStatus}
        participants={props.participants}
        lastReadIndex={props.lastReadIndex}
        use24hTimeFormat={props.use24hTimeFormat}
        handleDroppedFiles={props.onDroppedFilesChange}
        templateOnly={props.templateOnly}
      />

      <NewMessageInputField
        key={selectedSid}
        convoSid={selectedSid}
        id={props.id}
        client={props.client}
        messages={props.messages}
        convo={reduxConvo}
        typingData={props.typingData}
        droppedFiles={props.droppedFiles}
        templateOnly={props.templateOnly}
        inputDisabled={props.inputDisabled}
        draftValue={draftValue}
        onDraftChange={setDraftValue}
      />
    </>
  );
};

const NewConversationContent = React.memo(NewConversationContentInner);

export default NewConversationContent;
